import { useState, useRef, useEffect } from 'react';
import { QuestionIcon, VerifiedIcon, WhiteLogo } from 'icons';
import { MenuItem } from 'components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState, logout } from 'data';
import avatar from './../images/avatar.png';
import { useTranslation } from 'react-i18next';
import { useProfile } from 'hooks';
import { PrimeIcons } from 'primereact/api';

const NAV_ITEMS = [
  { href: '/', labelKey: 'navbar.dashboard', icon: PrimeIcons.HOME },
  { href: '/invoices', labelKey: 'navbar.invoices', icon: PrimeIcons.BOOK },
  { href: '/clients', labelKey: 'navbar.clients', icon: PrimeIcons.BUILDING },
  { href: '/documents', labelKey: 'navbar.documents', icon: PrimeIcons.FILE },
  { href: '/edit-profile', labelKey: 'navbar.settings', icon: PrimeIcons.COG },
];

export const Navbar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useProfile();
  const { avatar: avatarDoc } = useSelector(
    (state: RootState) => state.document,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const goTo = (href: string) => {
    setIsDropdownOpen(false);
    navigate(href);
  };

  return (
    <header
      className="app-header w-full lg:px-[136px] px-[16px] flex justify-between items-center bg-[#2E435F] fixed top-0 left-0 right-0"
      style={{ zIndex: 99999 }}
    >
      <div className="flex items-center gap-[24px] min-w-0">
        <button
          className="flex items-center object-contain cursor-pointer shrink-0"
          onClick={() => navigate('/')}
          aria-label={t('navbar.dashboard') as string}
        >
          <WhiteLogo />
        </button>
        {location.pathname !== '/edit-profile' && (
          <nav
            aria-label={t('navbar.sections') as string}
            className="hidden lg:flex items-center w-auto h-[24px] gap-[12px]"
          >
            {NAV_ITEMS.map((item) => (
              <MenuItem
                key={item.href}
                href={item.href}
                property1={t(item.labelKey)}
                isActive={location.pathname === item.href}
                icon={<i className={item.icon} />}
              />
            ))}
          </nav>
        )}
      </div>

      <div className="flex items-center w-auto gap-[8px] lg:gap-[16px]">
        <a
          className="h-11 w-11 flex items-center justify-center"
          href={`mailto:${t('dashboard.welcome.email')}`}
          aria-label={t('navbar.help') as string}
        >
          <QuestionIcon />
        </a>
        <div className="relative" ref={dropdownRef} style={{ zIndex: 99999 }}>
          <button
            onClick={toggleDropdown}
            aria-haspopup="menu"
            aria-expanded={isDropdownOpen}
            aria-label={t('navbar.account') as string}
            className="h-11 w-11 flex items-center justify-center rounded-full"
          >
            <img
              src={avatarDoc?.url || avatar}
              className="w-[40px] h-[40px] rounded-full object-cover"
              alt=""
            />
          </button>
          {isDropdownOpen && (
            <div
              role="menu"
              className="absolute top-[48px] right-0 w-[268px] max-w-[calc(100vw_-_32px)] bg-white border border-[#D6D6D6] shadow-lg rounded-[12px] overflow-hidden fade-in"
              style={{ zIndex: 99999 }}
            >
              <div className="px-[16px] py-[14px] border-b border-[#EAECEF]">
                <div className="flex flex-row items-center gap-2 flex-wrap">
                  <p className="text-[16px] font-[700] text-[#2E435F]">
                    {user?.firstName} {user?.lastName}
                  </p>
                  {user?.idCard && (
                    <span className="flex flex-row gap-1 items-center">
                      <VerifiedIcon />
                      <span className="text-[12px] leading-none font-[700]">
                        {t('navbar.verified')}
                      </span>
                    </span>
                  )}
                </div>
                <p className="text-[#757575] text-[14px] break-words mt-[4px]">
                  {user?.email}
                </p>
              </div>
              <button
                role="menuitem"
                onClick={() => goTo('/edit-profile')}
                className="w-full text-left px-[16px] min-h-[48px] text-[16px] text-[#4F5057] hover:bg-[#F5F7F9] active:bg-[#EAECEF]"
              >
                {t('navbar.settings')}
              </button>
              <button
                role="menuitem"
                onClick={handleLogout}
                className="w-full text-left px-[16px] min-h-[48px] text-[16px] text-[#4F5057] hover:bg-[#F5F7F9] active:bg-[#EAECEF] border-t border-[#EAECEF]"
              >
                {t('navbar.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
